import { useEffect, useRef, useState } from 'react';
import { C, DEGRADE_ACTION, OMBRE_ACTION, POLICE_TITRE, POLICE_CORPS } from '../../theme';
import { Etoile, Micro } from '../../icones';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { assembler, construireTranscription, type ResultatsDictee } from '../dictee';
import { preparerPhoto } from '../image';
import type { ElementBoite } from '../../api/types';

// --- Reconnaissance vocale du navigateur ---
//
// Ni `SpeechRecognition` ni son prefixe webkit ne figurent dans les types du DOM. On
// decrit ici le strict necessaire plutot que de forcer en `any`.

// `resultIndex` n'est VOLONTAIREMENT pas declare ici : Chrome Android le met a zero a
// chaque evenement, et s'en servir pour n'ajouter que « la suite » duplique la phrase
// (bug de production du 2026-08-25). Tout passe par construireTranscription, qui
// reconstruit depuis le debut. Voir src/app/dictee.ts.
interface EvenementDictee extends Event {
  results: ResultatsDictee;
}

interface MoteurDictee extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((e: EvenementDictee) => void) | null;
  onerror: ((e: Event) => void) | null;
  onend: (() => void) | null;
}

type FabriqueDictee = new () => MoteurDictee;

function fabriqueDictee(): FabriqueDictee | null {
  const global = window as unknown as {
    SpeechRecognition?: FabriqueDictee;
    webkitSpeechRecognition?: FabriqueDictee;
  };
  return global.SpeechRecognition ?? global.webkitSpeechRecognition ?? null;
}

type Mode = 'dicter' | 'ecrire' | 'photo';

function Onglet({
  libelle,
  actif,
  onClick,
  indisponible = false,
}: {
  libelle: string;
  actif: boolean;
  onClick: () => void;
  /** Presente mais inerte : un onglet qui DISPARAIT ressemble a une panne. */
  indisponible?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={indisponible ? undefined : onClick}
      aria-pressed={actif}
      aria-disabled={indisponible || undefined}
      style={{
        opacity: indisponible ? 0.45 : 1,
        cursor: indisponible ? 'default' : 'pointer',
        flex: 1,
        height: 38,
        borderRadius: 10,
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 800,
        fontFamily: 'inherit',
        background: actif ? C.surfaceFixe : 'transparent',
        color: actif ? C.corailFonceFixe : C.tertiaireFixe,
      }}
    >
      {libelle}
    </button>
  );
}

export function PanneauOrganiser({
  fermer,
  onOrganise,
  signaler,
}: {
  fermer: () => void;
  onOrganise: (element: ElementBoite) => void;
  signaler: (message: string) => void;
}) {
  const dicteeDisponible = fabriqueDictee() !== null;
  const [mode, setMode] = useState<Mode>(dicteeDisponible ? 'dicter' : 'ecrire');
  const [texte, setTexte] = useState('');
  const [ecoute, setEcoute] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [apercu, setApercu] = useState<string | null>(null);
  const [legende, setLegende] = useState('');
  const moteur = useRef<MoteurDictee | null>(null);
  /** Texte acquis AVANT la dictee en cours (saisie manuelle ou dictee precedente). */
  const avant = useRef('');
  /** Dernier segment definitif rendu par le moteur ; fige a l'arret. */
  const definitifCourant = useRef('');

  // Le moteur de dictee doit etre arrete au demontage : sans cela le micro reste ouvert
  // apres la fermeture du panneau, ce que le telephone signale par un point rouge.
  useEffect(() => {
    return () => {
      moteur.current?.abort();
      moteur.current = null;
    };
  }, []);

  const basculerDictee = () => {
    if (ecoute) {
      moteur.current?.stop();
      return;
    }
    const Fabrique = fabriqueDictee();
    if (Fabrique === null) return;

    const instance = new Fabrique();
    instance.lang = 'fr-FR';
    instance.continuous = true;
    instance.interimResults = true;

    // Ce qui etait deja dans le champ avant cette dictee : on repart de la, sans jamais
    // le perdre ni le reecrire.
    avant.current = texte.trim();
    definitifCourant.current = '';

    instance.onresult = (evenement) => {
      const { definitif, provisoire } = construireTranscription(evenement.results);
      definitifCourant.current = definitif;
      // Le provisoire est AFFICHE mais jamais fige : il change encore a chaque mot.
      setTexte(assembler(avant.current, definitif, provisoire));
    };

    instance.onerror = () => {
      setEcoute(false);
      signaler("La dictée n'a pas fonctionné. Tu peux écrire à la place.");
    };

    instance.onend = () => {
      // Chrome arrete souvent la reconnaissance de lui-meme, meme en mode continu. On
      // fige alors ce qui est acquis pour qu'une reprise reparte de la, et on jette le
      // provisoire, qui n'est pas fiable.
      avant.current = assembler(avant.current, definitifCourant.current);
      definitifCourant.current = '';
      setTexte(avant.current);
      setEcoute(false);
    };

    moteur.current = instance;
    setEcoute(true);
    instance.start();
  };

  const choisirPhoto = (fichier: File | null) => {
    setPhoto(fichier);
    if (apercu !== null) URL.revokeObjectURL(apercu);
    setApercu(fichier === null ? null : URL.createObjectURL(fichier));
  };

  const organiser = async () => {
    if (envoi) return;
    moteur.current?.stop();
    if (mode === 'photo') {
      if (photo === null) return;
      setEnvoi(true);
      try {
        // Reduite AVANT l'envoi : le modele n'a pas besoin de 50 megapixels.
        const reduite = await preparerPhoto(photo);
        const { item } = await api.deposerPhoto({ ...reduite, text: legende.trim() });
        await api.organiser(item.id);
        onOrganise(item);
      } catch (cause) {
        signaler(messageDErreur(cause));
        setEnvoi(false);
      }
      return;
    }
    const contenu = texte.trim();
    if (contenu === '') return;
    setEnvoi(true);
    try {
      const { item } = await api.deposer(contenu);
      await api.organiser(item.id);
      onOrganise(item);
    } catch (cause) {
      // Si le depot a reussi mais que la comprehension a echoue, le message est dans la
      // boite avec un statut d'echec : il reste rejouable de la, rien n'est perdu.
      signaler(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  const peutOrganiser = (mode === 'photo' ? photo !== null : texte.trim() !== '') && !envoi;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: C.voile,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        zIndex: 10,
      }}
    >
      <div onClick={envoi ? undefined : fermer} style={{ flex: 1 }} />

      <div
        className="apparition"
        style={{
          background: C.surfaceFixe,
          borderRadius: '24px 24px 0 0',
          padding: '18px 20px 24px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          position: 'relative',
        }}
      >
        <div
          style={{ width: 40, height: 4, borderRadius: 2, background: C.bordureFixe, alignSelf: 'center' }}
        />
        <div style={{ fontFamily: POLICE_TITRE, fontWeight: 400, fontSize: 21, color: C.encreFixe }}>
          Organise-moi ça
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Onglet
            libelle="Dicter"
            actif={mode === 'dicter'}
            indisponible={!dicteeDisponible}
            onClick={() => {
              moteur.current?.stop();
              setMode('dicter');
            }}
          />
          <Onglet
            libelle="Écrire"
            actif={mode === 'ecrire'}
            onClick={() => {
              moteur.current?.stop();
              setMode('ecrire');
            }}
          />
          <Onglet
            libelle="Photo"
            actif={mode === 'photo'}
            onClick={() => {
              moteur.current?.stop();
              setMode('photo');
            }}
          />
        </div>
        {/* L'onglet grise pose une question : on y repond ICI, a cote de lui.
            Auparavant l'onglet DISPARAISSAIT, et l'explication vivait dans « Ecrire » —
            une testeuse sous Firefox en a conclu que l'audio de Colette etait casse. */}
        {!dicteeDisponible ? (
          <div
            style={{
              background: C.champFixe,
              borderRadius: 12,
              padding: '10px 12px',
              fontSize: 12.5,
              color: C.secondaireFixe,
              lineHeight: 1.5,
              marginTop: -6,
            }}
          >
            La dictée n’existe pas dans ce navigateur — Firefox ne sait pas encore
            transcrire la parole. Ouvre Colette dans <strong>Chrome</strong> (Android,
            ordinateur) ou <strong>Safari</strong> (iPhone), ou écris ton message ici.
          </div>
        ) : null}


        {mode === 'photo' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label
              style={{
                border: `1.5px dashed ${C.bordureFixe}`,
                borderRadius: 14,
                minHeight: apercu === null ? 96 : undefined,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: 12,
                cursor: 'pointer',
              }}
            >
              {apercu === null ? (
                <>
                  <span style={{ fontSize: 14.5, fontWeight: 800, color: C.corailFonceFixe }}>
                    Choisir une photo
                  </span>
                  <span style={{ fontSize: 12.5, color: C.tertiaireFixe, textAlign: 'center' }}>
                    Une liste de fournitures, une affiche, un mot de l'école… Colette la lit.
                  </span>
                </>
              ) : (
                <img
                  src={apercu}
                  alt="Photo choisie"
                  style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 10 }}
                />
              )}
              <input
                type="file"
                accept="image/*"
                aria-label="Choisir une photo"
                onChange={(e) => choisirPhoto(e.target.files?.[0] ?? null)}
                style={{ display: 'none' }}
              />
            </label>
            <input
              value={legende}
              onChange={(e) => setLegende(e.target.value)}
              placeholder="Un mot sur cette photo ? (facultatif)"
              aria-label="Un mot sur cette photo ? (facultatif)"
              style={{
                width: '100%',
                height: 44,
                border: `1px solid ${C.bordureFixe}`,
                borderRadius: 12,
                padding: '0 12px',
                boxSizing: 'border-box',
                fontSize: 16,
                fontFamily: POLICE_CORPS,
                color: C.encreFixe,
                // Sans fond declare, un input prend le BLANC du navigateur — et l'encre de
                // Colette etant claire, on n'y voit plus ce qu'on tape.
                background: C.champFixe,
                outline: 'none',
              }}
            />
          </div>
        ) : mode === 'dicter' ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
              padding: '6px 0',
            }}
          >
            <button
              type="button"
              onClick={basculerDictee}
              aria-label={ecoute ? 'Arrêter la dictée' : 'Commencer à dicter'}
              className={ecoute ? 'pulsation' : undefined}
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: ecoute ? DEGRADE_ACTION : C.champFixe,
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Micro taille={30} couleur={ecoute ? '#FFFFFF' : C.corailFonceFixe} />
            </button>
            <div style={{ fontSize: 13, color: C.secondaireFixe }}>
              {ecoute ? 'Je t’écoute…' : 'Appuie sur le micro et parle'}
            </div>
            <div
              style={{
                width: '100%',
                minHeight: 66,
                background: C.champFixe,
                borderRadius: 12,
                padding: '12px 14px',
                boxSizing: 'border-box',
                fontSize: 14,
                color: texte === '' ? C.tertiaireFixe : C.encreFixe,
                lineHeight: 1.5,
              }}
            >
              {texte === '' ? 'Ce que tu dis apparaîtra ici.' : texte}
            </div>
          </div>
        ) : (
          <>
            <textarea
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              placeholder="ex. Jeudi je vais à Lyon pour une réunion, il faut le dossier et le chargeur…"
              style={{
                width: '100%',
                minHeight: 96,
                border: `1px solid ${C.bordureFixe}`,
                borderRadius: 12,
                padding: '12px 14px',
                boxSizing: 'border-box',
                fontSize: 16, // 16 px minimum : en dessous, iOS zoome au focus.
                fontFamily: POLICE_CORPS,
                color: C.encreFixe,
                resize: 'none',
                outline: 'none',
              }}
            />
          </>
        )}

        {/* Le seul geste de cette feuille qui APPELLE Colette, donc le seul qui coute.
            L'etoile et la phrase en dessous le disent : le credit du porteur est limite,
            et rien ne doit ressembler par accident a un appel gratuit.

            Desactive, il prend un corail delave FIXE : un jeton translucide serait
            invisible sur le blanc de la feuille, qui ne suit pas le theme. */}
        <button
          type="button"
          className="pressable"
          onClick={() => void organiser()}
          disabled={!peutOrganiser}
          style={{
            height: 52,
            border: 'none',
            borderRadius: 14,
            background: peutOrganiser ? DEGRADE_ACTION : '#F3C9BC',
            boxShadow: peutOrganiser ? OMBRE_ACTION : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontSize: 16,
            fontWeight: 800,
            fontFamily: 'inherit',
            color: '#FFFFFF',
            cursor: peutOrganiser ? 'pointer' : 'default',
          }}
        >
          <Etoile taille={18} couleur="#FFFFFF" epaisseur={1.8} />
          Organiser
        </button>
        <div
          style={{
            fontSize: 12,
            color: C.tertiaireFixe,
            textAlign: 'center',
            lineHeight: 1.45,
            marginTop: -6,
          }}
        >
          Ce geste appelle Colette. Tu valides ensuite chaque proposition.
        </div>

        {envoi ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.94)',
              borderRadius: '24px 24px 0 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}
          >
            {/* Le MEME disque qui respire que pendant la dictee : « Colette t'ecoute »
                et « Colette reflechit » se ressemblent, ce qui est juste — c'est le meme
                moment d'attente. Un anneau qui tourne disait « le systeme charge », pas
                « quelqu'un reflechit ». */}
            <div
              className="pulsation"
              style={{
                width: 70,
                height: 70,
                borderRadius: '50%',
                background: DEGRADE_ACTION,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Etoile taille={30} couleur="#FFFFFF" epaisseur={1.7} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.corailFonceFixe }}>
              Colette réfléchit…
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
